import { useRequiredContext } from "@redotech/react-util/context";
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

/**
 * Help-chat-specific overlay for code-search state. Kept separate from
 * the shared EscalationProvider so Ask Redo (which has no code-search
 * flow) isn't forced to carry these fields.
 *
 * Resets when the triage thread changes — wired by global-ai.tsx via
 * EscalationProvider's `onTriageThreadChange` callback so each new
 * triage thread gets a fresh shot at code search.
 */

export interface PendingCodeSearch {
  noticeTopic: string;
  threadId: string;
}

export interface CodeSearchState {
  codeSearchAttempted: boolean;
  pendingCodeSearch: PendingCodeSearch | null;
}

export interface CodeSearchContextType {
  state: CodeSearchState;
  update: (partial: Partial<CodeSearchState>) => void;
  reset: () => void;
}

const defaultState: CodeSearchState = {
  codeSearchAttempted: false,
  pendingCodeSearch: null,
};

export const CodeSearchContext = createContext<
  CodeSearchContextType | undefined
>(undefined);

export function useCodeSearchContext(): CodeSearchContextType {
  return useRequiredContext(CodeSearchContext);
}

export const CodeSearchProvider = memo(function CodeSearchProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<CodeSearchState>(defaultState);

  const update = useCallback((partial: Partial<CodeSearchState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const reset = useCallback(() => setState(defaultState), []);

  const value = useMemo(
    () => ({ state, update, reset }),
    [state, update, reset],
  );

  return (
    <CodeSearchContext.Provider value={value}>
      {children}
    </CodeSearchContext.Provider>
  );
});
